import React, { useEffect, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';

import { DepartmentsService } from 'components/services'; 
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import './styles/IntervalPickerDialog.css';

interface Interval {
  start: string;
  end: string;
  intervalOccupancy?: number;
  _fromForm?: boolean;
}

interface IntervalPickerDialogProps {
  visible: boolean;
  onHide: () => void;
  onSelect: (interval: Interval | null) => void;
  selectedInterval: Interval | null;
  departmentId?: string | number;
  taskTypeId?: string | number;
  selectedEmployees?: string[];
  disabled?: boolean;
  handleClearInterval: () => void
}

const IntervalPickerDialog: React.FC<IntervalPickerDialogProps> = ({
  visible,
  onHide,
  onSelect,
  selectedInterval,
  departmentId,
  taskTypeId,
  selectedEmployees = [],
  disabled = false,
  handleClearInterval
}) => {
  const getInitialDate = () => {
    if (selectedInterval) {
      return new Date(selectedInterval.start);
    }
    return new Date();
  };

  const [selectedDate, setSelectedDate] = useState<Date>(getInitialDate());
  const [tempSelectedInterval, setTempSelectedInterval] = useState<Interval | null>(selectedInterval);
  const [allIntervals, setAllIntervals] = useState<Interval[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);

  useEffect(() => {
    setTempSelectedInterval(selectedInterval);
  }, [selectedInterval]);

  useEffect(() => {
    if (selectedInterval) {
      const date = new Date(selectedInterval.start);
      setSelectedDate(date);
    }
  }, [selectedInterval]);

  const allIntervalsWithSelected = useMemo(() => {
    if (!selectedInterval) {
      return allIntervals;
    }
    
    const exists = allIntervals.some(
      (interval) => 
        interval.start === selectedInterval.start && 
        interval.end === selectedInterval.end
    );
    
    if (exists) {
      return allIntervals;
    }
    
    return [
      ...allIntervals,
      {
        ...selectedInterval,
        _fromForm: true
      }
    ];
  }, [allIntervals, selectedInterval]);

  const intervalsByDate = useMemo(() => {
    const grouped: Record<string, Interval[]> = {};
    
    allIntervalsWithSelected.forEach(interval => {
      const date = interval.start.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(interval);
    });
    
    return grouped;
  }, [allIntervalsWithSelected]);

  const hasIntervalsForDate = (date: Date): boolean => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const intervals = intervalsByDate[dateStr] || [];
    return intervals.length > 0;
  };

  const getIntervalsForDate = (date: Date): Interval[] => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return intervalsByDate[dateStr] || [];
  };

  const intervalsForSelectedDate = getIntervalsForDate(selectedDate);

  const dateTemplate = (date: any) => {
    const day = date.day;
    const isToday = date.day === new Date().getDate() && 
                    date.month === new Date().getMonth() && 
                    date.year === new Date().getFullYear();
    const isSelected = date.day === selectedDate.getDate() && 
                       date.month === selectedDate.getMonth() && 
                       date.year === selectedDate.getFullYear();
    
    const dateObj = new Date(date.year, date.month, date.day);
    const hasIntervals = hasIntervalsForDate(dateObj);
    
    const isDisabled = dateObj < minDate || dateObj > maxDate;
    
    return (
      <div 
        className={classNames('interval-picker-dialog__day', {
          'interval-picker-dialog__day--today': isToday,
          'interval-picker-dialog__day--selected': isSelected,
          'interval-picker-dialog__day--has-intervals': hasIntervals && !isDisabled,
          'interval-picker-dialog__day--disabled': isDisabled
        })}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <span>{day}</span>
        {hasIntervals && !isDisabled && (
          <span 
            className="interval-picker-dialog__day-dot"
            style={{
              position: 'absolute',
              bottom: '2px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: '#3C83F6'
            }}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!visible || !departmentId || !taskTypeId) {
      setAllIntervals([]);
      return;
    }

    const loadIntervals = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await DepartmentsService.getIntervalsByDepartment(
          Number(departmentId),
          taskTypeId,
          selectedEmployees.length > 0 ? selectedEmployees : undefined
        );
        
        if (response.data && response.data.length > 0) {
          setAllIntervals(response.data);
        } else {
          setAllIntervals([]);
          setError('Нет доступных интервалов');
        }
      } catch (err) {
        console.error('Ошибка при загрузке интервалов:', err);
        setError('Ошибка при загрузке интервалов');
        setAllIntervals([]);
      } finally {
        setLoading(false);
      }
    };

    loadIntervals();
  }, [visible, departmentId, taskTypeId, selectedEmployees]);

  useEffect(() => {
    if (tempSelectedInterval) {
      const selectedDateStr = tempSelectedInterval.start.split('T')[0];
      const newDateStr = selectedDate.toISOString().split('T')[0];
      
      if (selectedDateStr !== newDateStr) {
        const isFromForm = (tempSelectedInterval as any)._fromForm === true;
        const isSelectedInterval = selectedInterval && 
          tempSelectedInterval.start === selectedInterval.start &&
          tempSelectedInterval.end === selectedInterval.end;

        if (!isFromForm && !isSelectedInterval) {
          setTempSelectedInterval(null);
        }
      }
    }
  }, [selectedDate, selectedInterval]);

  const handleDateSelect = (e: any) => {
    if (e.value) {
      setSelectedDate(e.value);
    }
  };

  const handleIntervalSelect = (interval: Interval) => {
    setTempSelectedInterval(interval);
  };

  const handleConfirm = () => {
    if (tempSelectedInterval) {
      onSelect(tempSelectedInterval);
    }
    onHide();
  };

  const handleCancel = () => {
    setTempSelectedInterval(selectedInterval);
    onHide();
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const dialogStyle = {
    maxWidth: '500px',
    width: '90vw',
  };

  const footer = (
    <div className="flex gap-3">
      <div className="flex gap-3 ml-auto">
        <Button
          label="Выбрать"
          icon="pi pi-check"
          onClick={handleConfirm}
          disabled={!tempSelectedInterval}
        />
        <Button
          label="Отмена"
          icon="pi pi-times"
          onClick={handleCancel}
          className="p-button-secondary"
        />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleCancel}
      header="Выбор интервала"
      modal
      pt={withDialogFooterSpacing()}
      footer={footer}
      style={dialogStyle}
      breakpoints={{
        '960px': '95vw',
        '640px': '95vw'
      }}
      className="interval-picker-dialog"
    >
      <div className="interval-picker-dialog">
        <div className="interval-picker-dialog__calendar-wrapper">
          <Calendar
            value={selectedDate}
            onChange={handleDateSelect}
            inline
            minDate={minDate}
            maxDate={maxDate}
            dateFormat="dd.mm.yy"
            locale="ru"
            className="interval-picker-dialog__calendar"
            dateTemplate={dateTemplate}
          />
        </div>

        <Divider />

        <div className="interval-picker-dialog__intervals">
          <div className="interval-picker-dialog__intervals-header">
            <span className="interval-picker-dialog__intervals-date">
              {formatDate(selectedDate)}
            </span>
            <span className="interval-picker-dialog__intervals-count">
              {intervalsForSelectedDate.length} интервалов
            </span>
          </div>

          {loading ? (
            <div className="interval-picker-dialog__loading">
              <ProgressSpinner style={{ width: '40px', height: '40px' }} />
              <span>Загрузка интервалов...</span>
            </div>
          ) : error ? (
            <div className="interval-picker-dialog__error">
              <i className="pi pi-exclamation-circle" />
              <span>{error}</span>
            </div>
          ) : intervalsForSelectedDate.length > 0 ? (
            <div className="interval-picker-dialog__intervals-list">
              {intervalsForSelectedDate.map((interval, index) => {
                const isSelected = 
                  tempSelectedInterval?.start === interval.start &&
                  tempSelectedInterval?.end === interval.end;
                
                const isFromForm = (interval as any)._fromForm === true;
                
                return (
                  <div
                    key={index}
                    className={classNames('interval-picker-dialog__interval-item', {
                      'interval-picker-dialog__interval-item--selected': isSelected,
                      'interval-picker-dialog__interval-item--from-form': isFromForm
                    })}
                    onClick={() => handleIntervalSelect(interval)}
                  >
                    <div className="interval-picker-dialog__interval-time">
                      <i className="pi pi-clock mr-2" />
                      <span>{formatTime(interval.start)} - {formatTime(interval.end)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="interval-picker-dialog__no-intervals">
              <i className="pi pi-info-circle" />
              <span>
                {selectedEmployees.length > 0 
                  ? 'Нет доступных интервалов для выбранных исполнителей'
                  : 'Нет интервалов на выбранную дату'}
              </span>
            </div>
          )}

          {selectedEmployees.length > 0 && intervalsForSelectedDate.length > 0 && (
            <div className="interval-picker-dialog__filter-info">
              <i className="pi pi-filter mr-2" />
              <span>
                Показаны интервалы, доступные для {selectedEmployees.length} исполнителей
              </span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default IntervalPickerDialog;